// frontend/src/pages/EditItemPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { parseFormattedNumber, formatNumberInput, IMAGE_BASE_URL } from '../services/utils';

function EditItemPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    precio: 0,
    tipo_operacion: 'Venta',
    estado: 'Nuevo',
    facultad_id: '',
  });

  const [imagenFilename, setImagenFilename] = useState(null);
  const [originalImagenFilename, setOriginalImagenFilename] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [universidades, setUniversidades] = useState([]);
  const [facultades, setFacultades] = useState([]);
  const [selectedUniversidad, setSelectedUniversidad] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemRes, uniRes] = await Promise.all([
          api.get(`/items/${id}`),
          api.get('/general/universidades')
        ]);
        const itemData = itemRes.data;
        const uniData = uniRes.data;
        setUniversidades(uniData);
        setForm({
          titulo: itemData.titulo,
          descripcion: itemData.descripcion || '',
          precio: itemData.precio,
          tipo_operacion: itemData.tipo_operacion,
          estado: itemData.estado,
          facultad_id: itemData.facultad?.id || '', // Corregido para acceder al ID si viene el objeto
        });

        setImagenFilename(itemData.imagen_filename);
        setOriginalImagenFilename(itemData.imagen_filename);

        // Lógica para pre-seleccionar universidad si hay facultad
        if (itemData.facultad && itemData.facultad.id) {
            const facId = itemData.facultad.id;
            for (const uni of uniData) {
                if (uni.facultades.some(fac => fac.id === facId)) {
                    setSelectedUniversidad(String(uni.id));
                    setFacultades(uni.facultades);
                    break;
                }
            }
        } else if (itemData.facultad_id) {
            // Fallback si viene como ID plano
             for (const uni of uniData) {
                if (uni.facultades.some(fac => fac.id === itemData.facultad_id)) {
                    setSelectedUniversidad(String(uni.id));
                    setFacultades(uni.facultades);
                    break;
                }
            }
        }
      } catch (err) { setError('No se pudieron cargar los datos para editar.'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
     if (selectedUniversidad) {
        const uni = universidades.find(u => u.id === parseInt(selectedUniversidad));
        setFacultades(uni ? uni.facultades : []);
    } else {
        setFacultades([]);
    }
   }, [selectedUniversidad, universidades]);

   useEffect(() => {
     return () => {
       if (imagePreviewUrl) {
         URL.revokeObjectURL(imagePreviewUrl);
       }
     };
   }, [imagePreviewUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePriceChange = (e) => {
    const numberValue = parseFormattedNumber(e.target.value);
    if (!isNaN(numberValue)) {
      setForm(prev => ({ ...prev, precio: numberValue }));
    }
  };

  const handleUniversidadChange = (e) => {
     setSelectedUniversidad(e.target.value);
     setForm(prev => ({...prev, facultad_id: ''}));
   }

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    setImagePreviewUrl(previewUrl);

    setError(''); setIsUploading(true);
    try {
      // Paso 1: pedir la URL prefirmada al backend
      const presignedResponse = await api.get('/upload/presigned-url', {
        params: { content_type: file.type }
      });
      const { presigned_url, filename } = presignedResponse.data;

      // Paso 2: subir el archivo DIRECTAMENTE a R2 (sin pasar por Railway)
      await fetch(presigned_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      setImagenFilename(filename);
    } catch (err) {
        setError(err.response?.data?.detail || 'Error al subir la imagen.');
        URL.revokeObjectURL(previewUrl);
        setImagePreviewUrl(null);
        setImagenFilename(originalImagenFilename);
    }
    finally { setIsUploading(false); }
  };

  const handleRemoveImage = () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
      setImagenFilename(null);
  };

  // --- CORRECCIÓN PRINCIPAL AQUÍ ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Preparamos el objeto PLANO (sin anidar en item_data)
    const payload = {
      ...form,
      facultad_id: form.facultad_id ? parseInt(form.facultad_id) : null,
    };

    // Solo añadimos la imagen si cambió (para que el backend detecte el cambio)
    if (imagenFilename !== originalImagenFilename) {
        payload.imagen_filename = imagenFilename;
    }

    try {
      await api.put(`/items/${id}`, payload);
      navigate('/mis-publicaciones');
    } catch (err) {
      console.error("Error update:", err);
      setError(err.response?.data?.detail || 'Ocurrió un error al actualizar.');
    }
  };
  // ---------------------------------

  if (loading) return <p>Cargando datos para edición...</p>;

  let displayImageUrl = null;
  if (imagePreviewUrl) {
      displayImageUrl = imagePreviewUrl;
  } else if (imagenFilename) {
      displayImageUrl = `${IMAGE_BASE_URL}${imagenFilename}`;
  }

  return (
    <div className="row justify-content-center">
      <div className="col-md-8">
        <div className="card p-4 shadow-sm">
          <h2 className="text-center mb-4">Editar Producto</h2>
          {error && !loading && <div className="alert alert-danger">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="mb-3"><label className="form-label">Título</label><input type="text" name="titulo" className="form-control" value={form.titulo} onChange={handleChange} required /></div>
            <div className="mb-3"><label className="form-label">Descripción</label><textarea name="descripcion" className="form-control" rows="3" value={form.descripcion} onChange={handleChange}></textarea></div>

            <div className="mb-3">
              <label htmlFor="imageUpload" className="form-label">Imagen del Producto</label>

              {displayImageUrl ? (
                <div className="text-center my-2">
                    <img src={displayImageUrl} alt="Vista previa" className="img-fluid rounded" style={{ maxHeight: '200px', objectFit: 'cover' }} />
                </div>
              ) : (
                <div className="text-center my-2 p-3 bg-light rounded">
                    <small className="text-muted">Este producto no tiene imagen.</small>
                </div>
              )}

              <input className="form-control" type="file" id="imageUpload" accept="image/*" onChange={handleImageUpload} />
              {isUploading && <div className="form-text text-primary">Subiendo nueva imagen...</div>}

              {displayImageUrl && !isUploading && (
                  <button type="button" className="btn btn-outline-danger btn-sm mt-2" onClick={handleRemoveImage}>
                      Quitar Imagen
                  </button>
              )}
            </div>

            <div className="row">
              <div className="col-md-6 mb-3"><label className="form-label">Universidad</label><select className="form-select" value={selectedUniversidad} onChange={handleUniversidadChange}><option value="">Selecciona...</option>{universidades.map(uni => <option key={uni.id} value={uni.id}>{uni.nombre}</option>)}</select></div>
              <div className="col-md-6 mb-3"><label className="form-label">Facultad</label><select name="facultad_id" className="form-select" value={form.facultad_id} onChange={handleChange} disabled={!selectedUniversidad}><option value="">Selecciona...</option>{facultades.map(fac => <option key={fac.id} value={fac.id}>{fac.nombre}</option>)}</select></div>
            </div>
            <div className="row">
              <div className="col-md-4 mb-3"><label className="form-label">Precio (GS)</label><input type="text" name="precio" className="form-control" value={formatNumberInput(form.precio)} onChange={handlePriceChange} required /></div>
              <div className="col-md-4 mb-3"><label className="form-label">Operación</label><select name="tipo_operacion" className="form-select" value={form.tipo_operacion} onChange={handleChange}><option value="Venta">Venta</option><option value="Préstamo">Préstamo</option></select></div>
              <div className="col-md-4 mb-3"><label className="form-label">Estado</label><select name="estado" className="form-select" value={form.estado} onChange={handleChange}><option value="Nuevo">Nuevo</option><option value="Usado">Usado</option></select></div>
            </div>
            <div className="d-grid">
              <button type="submit" className="btn btn-primary btn-lg" disabled={isUploading}>
                {isUploading ? 'Subiendo imagen...' : 'Guardar Cambios'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
export default EditItemPage;
